import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  JobListSort,
  JobFilter,
  JobFilterType,
  JobFilterComparison,
} from '@ironsight/types'

import { JobStatus, ApprovalStatus } from '@/models/job'
import { map, parseInt, intersection } from 'lodash'
import { mergeInterval } from '@/utils/dateUtils'
import moment from 'moment'

/**
 * Adapter for FilteredJobStore for Components using Legacy Filter Formats.
 */
export function useFilteredJobStoreLegacyAdapter() {
  const filterStore = useFilteredJobStore()

  const DIVISION_KEY = 'OwnerId'
  const PRIORITY_KEY = 'Priority'
  const RESOURCETYPE_KEY = 'ResourceTypeId'
  const SERVICEPROVIDER_KEY = 'ServiceProviderId'
  const STATUS_KEY = 'Status'
  const ACTIVITY_KEY = 'TaskId'
  const LOCATION_KEY = 'LocationId'
  const START_KEY = 'Start'
  const END_KEY = 'End'
  const COMPLETED_TIME_KEY = 'CompletedTime'
  const START_TIME_KEY = 'StartTime'
  const jobStatuses = Object.values(JobStatus)
  const approvalStatuses = Object.values(ApprovalStatus)

  const selectedPriorities = computed({
    get: () => toLegacyFilters(PRIORITY_KEY).map((x) => parseInt(x, 10)),
    set: (values: number[]) => {
      fromLegacyFilters(PRIORITY_KEY, 'IntLookup', 'In', values)
    },
  })

  const selectedDivisions = computed({
    get: () => toLegacyFilters(DIVISION_KEY),
    set: (values: string[]) =>
      fromLegacyFilters(DIVISION_KEY, 'StringLookup', 'In', values),
  })

  const selectedServiceProviders = computed({
    get: () => toLegacyFilters(SERVICEPROVIDER_KEY),
    set: (values: any[]) => {
      if (values[0] === null) values[0] = ''
      return fromLegacyFilters(
        SERVICEPROVIDER_KEY,
        'StringLookup',
        'In',
        values
      )
    },
  })

  const selectedResourceTypes = computed({
    get: () => toLegacyFilters(RESOURCETYPE_KEY),
    set: (values: string[]) =>
      fromLegacyFilters(RESOURCETYPE_KEY, 'IntLookup', 'In', values),
  })

  const selectedJobStatuses = computed({
    get: () => {
      return intersection(toLegacyFilters(STATUS_KEY), jobStatuses)
    },
    set: (values: string[]) =>
      fromLegacyFilters(STATUS_KEY, 'StringLookup', 'In', values),
  })

  const selectedApprovalStatuses = computed({
    get: () => {
      return intersection(toLegacyFilters(STATUS_KEY), approvalStatuses)
    },
    set: (values: string[]) =>
      fromLegacyFilters(STATUS_KEY, 'StringLookup', 'In', values),
  })

  const selectedActivities = computed({
    get: () => toLegacyFilters(ACTIVITY_KEY),
    set: (values: string[]) =>
      fromLegacyFilters(ACTIVITY_KEY, 'StringLookup', 'In', values),
  })
  const selectedLocations = computed<(number | null)[]>({
    get: () =>
      toLegacyFilters(LOCATION_KEY).map((x) => {
        const parsed = parseInt(x, 10)
        return isNaN(parsed) ? null : parsed
      }),
    set: (values: (number | null)[]) =>
      fromLegacyFilters(LOCATION_KEY, 'IntLookup', 'In', values),
  })

  const selectedStart = computed<moment.Moment | null>(() => {
    const filter = toLegacyFilters(START_KEY)
    if (filter.length === 1) {
      return moment(filter[0])
    }
    return null
  })

  const selectedEnd = computed<moment.Moment | null>(() => {
    const filter = toLegacyFilters(END_KEY)
    if (filter.length === 1) {
      return moment(filter[0])
    }
    return null
  })

  const updateDateRange = (start: moment.Moment, end: moment.Moment) => {
    fromLegacyFilters(COMPLETED_TIME_KEY, 'DateTime', 'Between', [start, end])
    fromLegacyFilters(START_TIME_KEY, 'DateTime', 'Between', [start, end])
  }

  const clearDateRange = () => {
    fromLegacyFilters(COMPLETED_TIME_KEY, 'DateTime', 'Between', [])
    fromLegacyFilters(START_TIME_KEY, 'DateTime', 'Between', [])
  }

  const hasDateRange = computed(() => {
    return (
      toLegacyFilters(COMPLETED_TIME_KEY).length > 0 ||
      toLegacyFilters(START_TIME_KEY).length > 0
    )
  })

  const dateRangeLabel = computed(() => {
    return hasDateRange.value
      ? `${
          selectedEnd.value!.diff(selectedStart.value!, 'days') + 1
        } dates selected`
      : 'Showing all dates'
  })

  /**
   * Returns the values in Legacy format.
   *
   * @param key : string
   *  The property name of the filter.
   * @returns string[]
   *  The array of values
   */
  const toLegacyFilters = (key: string): string[] => {
    const filters = filterStore.getFilters
    if (key === START_KEY || key === END_KEY) {
      const completedTime = filters.find(
        (x) => x.property === COMPLETED_TIME_KEY
      )
      const startTime = filters.find((x) => x.property === START_TIME_KEY)
      const mergedInterval = mergeInterval(
        startTime?.value,
        completedTime?.value
      )
      if (mergedInterval.length === 2) {
        const index = key === START_KEY ? 0 : 1
        return [mergedInterval[index]]
      }
      return []
    }
    const idx = filters.findIndex((x) => x.property === key)
    if (idx === -1) return []

    if (filters[idx].value?.[0]?.value !== undefined) {
      // if the value is 'null' or '' then map it to null
      return map(filters[idx].value, (item) => {
        const v = item.value
        return v === 'null' || v === '' ? null : v
      })
    }

    return filters[idx].value
  }

  /**
   * Sets the values in Legacy format to the JobFilters (`JobFilters.ts`).
   *
   * @param key : string
   *  The property name of the filter.
   * @param filterType : JobFilterType
   *  The type of filter.
   * @param comparisonType : JobFilterComparison
   *  The type of comparison.
   * @returns string[]
   *  The array of values
   */
  const fromLegacyFilters = (
    key: string,
    filterType: JobFilterType,
    comparisonType: JobFilterComparison,
    values: any[],
    hidden?: boolean
  ) => {
    const filters = filterStore.getFilters
    const idx = filters.findIndex((x) => x.property === key)
    if (values.length === 0 && idx !== -1) {
      filterStore.removeFilter(filters[idx].id)
    } else if (values.length > 0) {
      if (key === PRIORITY_KEY) {
        values = values.map((v) => parseInt(v, 10))
      }
      filterStore.setFilter({
        id: idx !== -1 ? filters[idx].id : crypto.randomUUID(),
        property: key,
        value: values,
        type: filterType,
        comparison: comparisonType,
        hidden,
      })
    }
  }

  function $patch(payload: Record<string, any>, hidden?: boolean) {
    if ('selectedPriorities' in payload && selectedPriorities) {
      fromLegacyFilters(
        PRIORITY_KEY,
        'IntLookup',
        'In',
        payload.selectedPriorities,
        hidden
      )
    }

    if ('selectedDivisions' in payload && selectedDivisions) {
      fromLegacyFilters(
        DIVISION_KEY,
        'StringLookup',
        'In',
        payload.selectedDivisions,
        hidden
      )
    }

    if ('selectedServiceProviders' in payload && selectedServiceProviders) {
      fromLegacyFilters(
        SERVICEPROVIDER_KEY,
        'StringLookup',
        'In',
        payload.selectedServiceProviders,
        hidden
      )
    }

    if ('selectedResourceTypes' in payload && selectedResourceTypes) {
      fromLegacyFilters(
        RESOURCETYPE_KEY,
        'IntLookup',
        'In',
        payload.selectedResourceTypes,
        hidden
      )
    }

    if ('selectedActivities' in payload && selectedActivities) {
      fromLegacyFilters(
        ACTIVITY_KEY,
        'StringLookup',
        'In',
        payload.selectedActivities,
        hidden
      )
    }

    if ('selectedJobStatuses' in payload && selectedJobStatuses) {
      fromLegacyFilters(
        STATUS_KEY,
        'StringLookup',
        'In',
        payload.selectedJobStatuses,
        hidden
      )
    }

    if ('selectedApprovalStatuses' in payload && selectedApprovalStatuses) {
      fromLegacyFilters(
        STATUS_KEY,
        'StringLookup',
        'In',
        payload.selectedApprovalStatuses,
        hidden
      )
    }

    if ('selectedLocations' in payload && selectedLocations) {
      fromLegacyFilters(
        LOCATION_KEY,
        'IntLookup',
        'In',
        payload.selectedLocations,
        hidden
      )
    }
  }

  return {
    selectedPriorities,
    selectedDivisions,
    selectedServiceProviders,
    selectedResourceTypes,
    selectedJobStatuses,
    selectedApprovalStatuses,
    selectedActivities,
    selectedLocations,
    selectedStart,
    selectedEnd,
    updateDateRange,
    clearDateRange,
    hasDateRange,
    dateRangeLabel,
    $patch,
  }
}

function normalizeFilter(filter: JobFilter): JobFilter {
  if (filter.type === 'DateTime' && Array.isArray(filter.value)) {
    return {
      ...filter,
      value: filter.value.map((dateValue) =>
        typeof dateValue === 'string' ? new Date(dateValue) : dateValue
      ),
    }
  }
  return filter
}

export const useFilteredJobStore = defineStore(
  'filtered-jobs',
  () => {
    const filters = ref<JobFilter[]>([])
    const pendingFilter = ref<JobFilter | undefined>(undefined)
    const isEnabled = ref<boolean>(true)
    const displayBanner = ref<boolean>(true)

    const sorting = ref<JobListSort>({
      field: 'Created',
      direction: 'desc',
    })

    const getVisibleFilters = computed(() => {
      let visible: JobFilter[]

      if (pendingFilter.value !== undefined) {
        const idx = filters.value.findIndex(
          (f) => f.id === pendingFilter.value!.id
        )
        if (idx !== -1) {
          const updated = [...filters.value]
          updated[idx] = pendingFilter.value
          visible = updated
        } else {
          visible = [pendingFilter.value, ...filters.value]
        }
      } else {
        visible = filters.value
      }

      return visible.filter((f) => !f.hidden)
    })

    const getAllFilters = computed(() => {
      let visible: JobFilter[]

      if (pendingFilter.value !== undefined) {
        const idx = filters.value.findIndex(
          (f) => f.id === pendingFilter.value!.id
        )
        if (idx !== -1) {
          const updated = [...filters.value]
          updated[idx] = pendingFilter.value
          visible = updated
        } else {
          visible = [pendingFilter.value, ...filters.value]
        }
      } else {
        visible = filters.value
      }

      return visible
    })

    const getVisibleFilterById = computed(() => {
      return (id: string) =>
        getVisibleFilters.value.find((filter) => filter.id === id)
    })

    function setPendingFilter(filter: JobFilter | undefined) {
      pendingFilter.value = filter
    }

    function removeFilter(id: string) {
      const index = filters.value.findIndex((filter) => filter.id === id)

      if (index !== -1) {
        filters.value.splice(index, 1)
      } else if (id === pendingFilter.value?.id) {
        pendingFilter.value = undefined
      }
    }

    function setFilter(filter: JobFilter) {
      pendingFilter.value = undefined
      const index = filters.value.findIndex((f) => f.id === filter.id)
      if (index !== -1) {
        filters.value[index].value = filter.value
        filters.value[index].hidden = filter.hidden
        filters.value[index].comparison = filter.comparison
      } else {
        filters.value.push(filter)
      }
    }

    function updateSorting(newSorting: JobListSort) {
      sorting.value = newSorting
    }

    function toggleEnabled() {
      isEnabled.value = !isEnabled.value
    }

    function toggleBanner() {
      displayBanner.value = !displayBanner.value
    }

    return {
      getFilters: filters,
      getPendingFilter: pendingFilter,
      setFilter,
      removeFilter,
      getSorting: sorting,
      updateSorting,
      pendingFilter,
      setPendingFilter,
      getVisibleFilters,
      getVisibleFilterById,
      getAllFilters,
      isEnabled,
      toggleEnabled,
      displayBanner,
      toggleBanner,
    }
  },
  {
    persist: {
      beforeRestore: () => {
        return new Promise((resolve) => {
          requestAnimationFrame(resolve)
        })
      },
      afterRestore: (ctx) => {
        if (ctx.store.getFilters?.length) {
          ctx.store.getFilters = map(ctx.store.getFilters, normalizeFilter)
        }
      },
    },
  }
)
